import { Controller, type Path } from 'react-hook-form'
import dayjs from 'dayjs'
import { cn } from '@/lib/utils'
import { addHmm } from '@/lib/flight-log-utils'
import type { FlightLogBroughtForwardSource } from '@@api/flightLog.shared'
import type { FlightLogFormData } from '../form'
import type { FlightLogSectionContext } from '../useFlightLogForm'
import { Button } from '../../ui/button'
import { BareHmmInput, BareNumInput } from '../../ui/prntr-fields'
import { SectionLabel } from '../../ui/prntr-input'
import { HoursTable } from '../HoursTable'

// Centered matrix cell editors. Locked/read-only cells render as plain text.
function HmmCell({
  context,
  name,
  locked,
  error,
}: {
  context: FlightLogSectionContext
  name: Path<FlightLogFormData>
  locked: boolean
  error?: boolean
}) {
  return (
    <Controller
      control={context.control}
      name={name}
      render={({ field }) =>
        locked ? (
          <span className="text-center text-sm font-semibold text-content-primary-subtle">
            {(field.value as string | null) ?? '0:00'}
          </span>
        ) : (
          <BareHmmInput
            value={(field.value as string | null) ?? null}
            onChange={field.onChange}
            onBlur={field.onBlur}
            align="center"
            className={cn(error && 'text-content-negative-bold')}
          />
        )
      }
    />
  )
}

function NumCell({
  context,
  name,
  locked,
}: {
  context: FlightLogSectionContext
  name: Path<FlightLogFormData>
  locked: boolean
}) {
  return (
    <Controller
      control={context.control}
      name={name}
      render={({ field }) =>
        locked ? (
          <span className="text-center text-sm font-semibold text-content-primary-subtle">
            {(field.value as number | null) ?? '—'}
          </span>
        ) : (
          <BareNumInput
            value={(field.value as number | null) ?? null}
            onChange={field.onChange}
            onBlur={field.onBlur}
            integer
            align="center"
          />
        )
      }
    />
  )
}

// The stored `hours` block renders as two sections: airframe + engines, and
// APU on its own (flight ops asked for them not to share a table).
export function HoursSection({
  context,
  part,
  broughtForwardSource,
  broughtForwardMatches,
  onApplyBroughtForward,
}: {
  context: FlightLogSectionContext
  part: 'airframeEngines' | 'apu'
  // The flight whose New Totals seeded the Brought Fwd cells; null on a
  // first-ever log for the aircraft
  broughtForwardSource: FlightLogBroughtForwardSource | null
  // False when this log's Brought Fwd cells no longer equal that flight's
  // New Totals (draft seeded earlier, or hand-edited)
  broughtForwardMatches: boolean
  // Replaces every Brought Fwd cell (both sections) from the previous flight
  onApplyBroughtForward: () => void
}) {
  const { values, readOnly, fieldError } = context

  const hourSources: readonly (readonly [string, string, (typeof values.hours)['apu']])[] =
    part === 'apu'
      ? [['APU', 'hours.apu', values.hours.apu]]
      : [
          ['Airframe', 'hours.airframe', values.hours.airframe],
          ...values.hours.engines.map(
            (cell, i) => [`Eng ${i + 1}`, `hours.engines.${i}`, cell] as const
          ),
        ]
  const hourRows = hourSources.map(([label, path, cell]) => ({
    key: path,
    label,
    broughtFwd: (
      <HmmCell
        context={context}
        name={`${path}.broughtFwd` as Path<FlightLogFormData>}
        locked={readOnly}
        error={!!fieldError(`${path}.broughtFwd`)}
      />
    ),
    thisLeg: (
      <HmmCell
        context={context}
        name={`${path}.thisLeg` as Path<FlightLogFormData>}
        locked={readOnly}
        error={!!fieldError(`${path}.thisLeg`)}
      />
    ),
    newTotal: addHmm(cell?.broughtFwd || '0:00', cell?.thisLeg || '0:00'),
  }))

  const cycleSources: readonly (readonly [
    string,
    string,
    (typeof values.hours.cycles)['apu'],
  ])[] =
    part === 'apu'
      ? [['APU', 'hours.cycles.apu', values.hours.cycles.apu]]
      : [
          ...values.hours.cycles.engines.map(
            (cell, i) => [`Eng ${i + 1}`, `hours.cycles.engines.${i}`, cell] as const
          ),
          ['Landing', 'hours.cycles.landings', values.hours.cycles.landings],
        ]
  const cycleRows = cycleSources.map(([label, path, cell]) => ({
    key: path,
    label,
    broughtFwd: (
      <NumCell
        context={context}
        name={`${path}.broughtFwd` as Path<FlightLogFormData>}
        locked={readOnly}
      />
    ),
    thisLeg: (
      <NumCell
        context={context}
        name={`${path}.thisLeg` as Path<FlightLogFormData>}
        locked={readOnly}
      />
    ),
    newTotal: String((cell?.broughtFwd ?? 0) + (cell?.thisLeg ?? 0)),
  }))

  return (
    <>
      {broughtForwardSource && (
        <div className="flex flex-col gap-2 py-2">
          <p className="text-xs text-content-primary-subtle">
            Brought forward from Log #{broughtForwardSource.logNumber} ·{' '}
            {broughtForwardSource.from} → {broughtForwardSource.to} ·{' '}
            {dayjs(broughtForwardSource.departureDateTime).format('D MMM YYYY')}
          </p>
          {!broughtForwardMatches && !readOnly && (
            <>
              <p className="text-xs text-content-notice-bold">
                This log's Brought Fwd totals differ from that flight's New Totals.
              </p>
              <Button
                type="button"
                variant="secondary"
                size="mini"
                className="w-fit"
                onClick={onApplyBroughtForward}
              >
                Update from previous flight
              </Button>
            </>
          )}
        </div>
      )}
      <SectionLabel>{part === 'apu' ? 'APU hours — H:MM' : 'Airframe / engine — H:MM'}</SectionLabel>
      <HoursTable rows={hourRows} />
      <SectionLabel>{part === 'apu' ? 'APU cycles' : 'Cycles & landings'}</SectionLabel>
      <HoursTable rows={cycleRows} />
    </>
  )
}
